import { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceArea, Dot,
} from 'recharts';
import { getInflammation } from './api';

// Заливка зон тяжести — светлая, чтобы линия и точки читались поверх.
const SEVERITY_FILL = {
  none: '#e6f0e0',
  mild: '#f3eed2',
  moderate: '#f9e6cc',
  severe: '#f7d8cb',
  extreme: '#f4cccc',
};

// Цвет точки и числа — насыщенный вариант той же зоны.
const SEVERITY_COLOR = {
  none: '#4f9c41',
  mild: '#a8951f',
  moderate: '#d9a03c',
  severe: '#d9703c',
  extreme: '#c2553f',
};

// Кварталы нарезаны по 13 недель; в «длинном» ISO-году 53-я неделя уходит в 4Q.
const QUARTERS = [1, 2, 3, 4];

function quarterBounds(q, weeksInYear) {
  return {
    from: (q - 1) * 13 + 1,
    to: q === 4 ? weeksInYear : q * 13,
  };
}

function severityOf(total, levels) {
  const hit = levels.find(l => total >= l.min && total <= l.max);
  return hit ? hit.key : 'none';
}

/** Верх оси — потолок зоны, следующей за той, куда попал максимум. */
function axisTop(maxTotal, levels) {
  if (levels.length === 0) return 36;
  const idx = levels.findIndex(l => maxTotal >= l.min && maxTotal <= l.max);
  const next = levels[Math.min((idx < 0 ? 0 : idx) + 1, levels.length - 1)];
  return next.max;
}

/** Деления по границам зон — так видно, где проходят пороги. */
function axisTicks(top, levels) {
  const ticks = [0, ...levels.map(l => l.min).filter(m => m > 0 && m < top), top];
  return [...new Set(ticks)].sort((a, b) => a - b);
}

function StatRow({ label, value, severityKey, levels, labelByKey }) {
  return (
    <div className="infl-stat">
      <span className="infl-stat-label">{label}</span>
      <span
        className="infl-stat-value"
        style={{ color: severityKey ? SEVERITY_COLOR[severityKey] : '#5a4a35' }}
      >
        {value}
      </span>
      <span className="infl-stat-sev">
        {severityKey ? labelByKey[severityKey] : ''}
      </span>
    </div>
  );
}

/** Окно с обоснованием: из чего сложился балл за неделю. */
function WeekDetailSheet({ week, labelByKey, onClose }) {
  // В причинах — только то, что дало балл. Находки, не дотянувшие до порога
  // (одна слизь, покраснения), показываем отдельно: молча терять их нельзя.
  const rows = week.params.filter(p => p.score > 0);
  const belowThreshold = week.params.filter(p => p.score === 0 && p.detail.length > 0);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">
            Неделя {week.week} · {week.date_from} — {week.date_to}
          </span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="infl-sheet-total">
          <span
            className="infl-sheet-num"
            style={{ color: SEVERITY_COLOR[week.severity] }}
          >
            {week.total}
          </span>
          <div>
            <div className="infl-sheet-sev">{labelByKey[week.severity]}</div>
            <div className="infl-sheet-hint">сумма 12 параметров</div>
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="infl-detail-empty">
            {belowThreshold.length > 0
              ? 'Ни один параметр не набрал балла'
              : 'За эту неделю ничего не отмечено'}
          </p>
        ) : (
          <div className="infl-detail-list">
            {rows.map(p => (
              <div key={p.key} className="infl-detail-row">
                <div className="infl-detail-param">
                  <span className="infl-detail-name">{p.name}</span>
                  <span className="infl-detail-score">{p.score}</span>
                </div>
                {p.detail.map((d, i) => (
                  <div key={i} className="infl-detail-fact">{d}</div>
                ))}
              </div>
            ))}
          </div>
        )}

        {belowThreshold.length > 0 && (
          <div className="infl-below">
            <div className="infl-below-title">Отмечено, но балла не дало</div>
            {belowThreshold.map(p => (
              <div key={p.key} className="infl-below-row">
                <span className="infl-below-name">{p.name}</span>
                {p.detail.join('; ')}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InflammationBlock({ petId }) {
  const [range, setRange] = useState(null);   // null = последние 8 недель
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWeek, setActiveWeek] = useState(null);

  useEffect(() => {
    setLoading(true);
    getInflammation(petId, range || {})
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [petId, range]);

  const levels = data?.severity_levels || [];
  const labelByKey = Object.fromEntries(levels.map(l => [l.key, l.label]));

  // Будущие недели выбранного диапазона считать не по чему — не рисуем.
  const chartData = (data?.weeks || []).filter(w => !w.is_future);

  const maxTotal = chartData.reduce((m, w) => Math.max(m, w.total), 0);
  const yTop = axisTop(maxTotal, levels);
  const yTicks = axisTicks(yTop, levels);

  // «По умолчанию» — серверное окно из 8 недель по текущую, поэтому range=null.
  const isDefault = range === null;
  const isWholeYear = !!data && !isDefault
    && data.week_from === 1 && data.week_to === data.weeks_in_year;
  const activeQuarter = !data || isDefault ? null : QUARTERS.find(q => {
    const b = quarterBounds(q, data.weeks_in_year);
    return data.week_from === b.from && data.week_to === b.to;
  });
  // Последние 8 недель имеют смысл только для идущего года
  const isCurrentYear = !!data && data.year === data.current_year;

  const applyQuarter = (q) => {
    const b = quarterBounds(q, data.weeks_in_year);
    setRange({ year: data.year, weekFrom: b.from, weekTo: b.to });
  };

  // В recharts 3 обработчик графика получает activeIndex/activeLabel;
  // activePayload из второй версии больше не передаётся.
  const pickWeek = (state) => {
    const idx = Number(state?.activeIndex ?? state?.activeTooltipIndex);
    if (Number.isInteger(idx) && chartData[idx]) return chartData[idx];
    const label = state?.activeLabel;
    return chartData.find(w => String(w.week) === String(label)) || null;
  };

  return (
    <div className="ss-block">
      <div className="ss-header">
        <div className="ss-title">Хроническое воспаление</div>
      </div>

      {loading && <p className="fr-empty">Загрузка…</p>}

      {!loading && !data && (
        <p className="fr-empty">Не удалось загрузить данные</p>
      )}

      {!loading && data && (
        <>
          {/* Сводка: всегда про «сейчас», независимо от выбранного окна */}
          <div className="infl-stats">
            <StatRow
              label="Текущая неделя"
              value={data.current_total}
              severityKey={severityOf(data.current_total, levels)}
              levels={levels}
              labelByKey={labelByKey}
            />
            <StatRow
              label="Прошлая неделя"
              value={data.previous_total}
              severityKey={severityOf(data.previous_total, levels)}
              levels={levels}
              labelByKey={labelByKey}
            />
            <StatRow
              label="Медиана за 8 недель"
              value={data.median_8w ?? '—'}
              severityKey={null}
              levels={levels}
              labelByKey={labelByKey}
            />
          </div>
          <p className="infl-live-note">
            Текущая неделя ещё считается — итог будет в воскресенье вечером.
          </p>

          {/* Год и диапазон недель */}
          <div className="infl-filter">
            {data.available_years.length > 1 && (
              <div className="visits-period-tabs infl-years">
                {data.available_years.map(y => (
                  <button
                    key={y}
                    className={`visits-tab${data.year === y ? ' visits-tab-active' : ''}`}
                    onClick={() => setRange({
                      year: y, weekFrom: 1, weekTo: data.weeks_in_year,
                    })}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}

            <div className="visits-period-tabs infl-presets">
              {QUARTERS.map(q => (
                <button
                  key={q}
                  className={`visits-tab${activeQuarter === q ? ' visits-tab-active' : ''}`}
                  onClick={() => applyQuarter(q)}
                >
                  {q}Q
                </button>
              ))}
              <button
                className={`visits-tab${isWholeYear ? ' visits-tab-active' : ''}`}
                onClick={() => setRange({
                  year: data.year, weekFrom: 1, weekTo: data.weeks_in_year,
                })}
              >
                год
              </button>
              {isCurrentYear && (
                <button
                  className={`visits-tab${isDefault ? ' visits-tab-active' : ''}`}
                  onClick={() => setRange(null)}
                >
                  по умол.
                </button>
              )}
            </div>
          </div>

          {chartData.length === 0 && (
            <p className="fr-empty">В выбранном диапазоне нет завершённых недель</p>
          )}

          {chartData.length > 0 && (
            <>
              <ResponsiveContainer width="100%" height={230}>
                <LineChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                  onClick={(state) => {
                    const w = pickWeek(state);
                    if (w) setActiveWeek(w);
                  }}
                >
                  {/* Зоны тяжести заливкой во всю ширину области графика */}
                  {levels.map(l => (
                    <ReferenceArea
                      key={l.key}
                      y1={l.min === 0 ? 0 : l.min - 0.5}
                      y2={l.max + 0.5}
                      fill={SEVERITY_FILL[l.key]}
                      fillOpacity={1}
                      ifOverflow="hidden"
                    />
                  ))}
                  {/* Подсказку не рисуем — её заменяет окно по тапу, но сам
                      Tooltip нужен: без него график не отслеживает активную
                      точку, а вертикальная черта показывает, куда попал палец. */}
                  <Tooltip
                    content={() => null}
                    cursor={{ stroke: '#8b7355', strokeDasharray: '3 3' }}
                  />
                  <CartesianGrid strokeDasharray="3 3" stroke="#00000012" vertical={false} />
                  <XAxis
                    dataKey="week"
                    tick={{ fontSize: 10, fill: '#777' }}
                    interval="preserveStartEnd"
                    minTickGap={4}
                  />
                  <YAxis
                    domain={[0, yTop]}
                    ticks={yTicks}
                    tick={{ fontSize: 10, fill: '#777' }}
                    width={30}
                    allowDecimals={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#5a4a35"
                    strokeWidth={2}
                    isAnimationActive={false}
                    dot={(props) => (
                      <Dot
                        key={props.payload.week}
                        cx={props.cx}
                        cy={props.cy}
                        r={4}
                        fill={SEVERITY_COLOR[props.payload.severity]}
                        stroke="#fff"
                        strokeWidth={1.5}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setActiveWeek(props.payload)}
                      />
                    )}
                    activeDot={{ r: 7, stroke: '#fff', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>

              <p className="ss-note infl-hint">
                Ось X — номер календарной недели, ось Y — сумма 12 параметров.
                Нажмите на точку, чтобы увидеть, из чего сложился балл.
              </p>

              <div className="infl-legend">
                {levels.map(l => (
                  <span key={l.key} className="infl-legend-item">
                    <span
                      className="infl-legend-swatch"
                      style={{ background: SEVERITY_FILL[l.key] }}
                    />
                    {l.min}–{l.max} {l.label.replace(' степень тяжести', '')}
                  </span>
                ))}
              </div>
            </>
          )}

          {activeWeek && (
            <WeekDetailSheet
              week={activeWeek}
              labelByKey={labelByKey}
              onClose={() => setActiveWeek(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
